import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiCheck,
  FiChevronDown,
  FiPlus,
  FiSearch,
  FiUser,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";

import {
  getCustomerMobile,
  getCustomerName,
} from "./constants";

const CustomerSearchSelect = ({
  value = "",
  selectedCustomer = null,
  onChange,
  onAddCustomer,
  disabled = false,
  error = "",
}) => {
  const api = useMemo(() => new API(), []);

  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const requestRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState("");

  const fetchCustomers = useCallback(
    async (searchValue = "") => {
      const currentRequest = requestRef.current + 1;

      requestRef.current = currentRequest;

      try {
        setLoading(true);
        setRequestError("");

        const payload = {
          page: 1,
          limit: 20,
          search: String(searchValue || "").trim(),
        };

        const res = await api.post(
          config.getCustomers,
          payload
        );

        if (currentRequest !== requestRef.current) {
          return;
        }

        if (res?.status === "success") {
          const records = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.data)
              ? res.data.data
              : [];

          setCustomers(records);
        } else {
          setCustomers([]);

          setRequestError(
            res?.message ||
              "Unable to fetch customers."
          );
        }
      } catch (errorResponse) {
        if (currentRequest !== requestRef.current) {
          return;
        }

        console.error(
          "Customer search error:",
          errorResponse
        );

        setCustomers([]);

        setRequestError(
          errorResponse?.response?.data?.message ||
            errorResponse?.message ||
            "Unable to fetch customers."
        );
      } finally {
        if (currentRequest === requestRef.current) {
          setLoading(false);
        }
      }
    },
    [api]
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchCustomers(search);
    }, 350);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [open, search, fetchCustomers]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setRequestError("");
    }
  }, [open]);

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    setOpen((previous) => !previous);
  };

  const handleSelect = (customer) => {
    if (typeof onChange === "function") {
      onChange(customer);
    }

    setOpen(false);
    setSearch("");
  };

  const handleClear = (event) => {
    event.stopPropagation();

    if (typeof onChange === "function") {
      onChange(null);
    }

    setSearch("");
  };

  const handleAddCustomer = () => {
    const normalizedSearch = String(
      search || ""
    ).trim();

    const isMobileSearch =
      /^\d+$/.test(normalizedSearch);

    const initialValues = {
      customerName: isMobileSearch
        ? ""
        : normalizedSearch,

      mobile: isMobileSearch
        ? normalizedSearch.slice(0, 10)
        : "",
    };

    setOpen(false);

    if (typeof onAddCustomer === "function") {
      onAddCustomer(initialValues);
    }
  };

  const displayCustomer = selectedCustomer || null;

  const displayName = displayCustomer
    ? getCustomerName(displayCustomer)
    : "";

  const displayMobile = displayCustomer
    ? getCustomerMobile(displayCustomer)
    : "";

  return (
    <div
      ref={containerRef}
      className="customer-search-select"
    >
      <button
        type="button"
        className={`customer-search-trigger ${
          error
            ? "customer-search-trigger-error"
            : ""
        }`}
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className="customer-search-trigger-content">
          {value && displayCustomer ? (
            <>
              <div className="customer-search-avatar">
                {String(displayName || "C")
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div className="customer-search-selected-info">
                <strong title={displayName}>
                  {displayName}
                </strong>

                <span>
                  {displayMobile}
                </span>
              </div>
            </>
          ) : (
            <>
              <FiUser />

              <span>
                Select or search customer
              </span>
            </>
          )}
        </div>

        <div className="customer-search-trigger-actions">
          {value ? (
            <span
              role="button"
              tabIndex={0}
              className="customer-search-clear"
              onClick={handleClear}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  handleClear(event);
                }
              }}
              aria-label="Clear selected customer"
            >
              <FiX />
            </span>
          ) : null}

          <FiChevronDown
            className={
              open
                ? "customer-search-chevron open"
                : "customer-search-chevron"
            }
          />
        </div>
      </button>

      {error ? (
        <small className="customer-search-field-error">
          {error}
        </small>
      ) : null}

      {open ? (
        <div className="customer-search-dropdown">
          <div className="customer-search-input-wrapper">
            <FiSearch />

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search customer name or mobile"
              autoFocus
            />

            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <FiX />
              </button>
            ) : null}
          </div>

          <div
            className="customer-search-results"
            role="listbox"
          >
            {loading ? (
              <div className="customer-search-loading">
                <span className="customer-search-spinner" />

                <span>
                  Searching customers...
                </span>
              </div>
            ) : requestError ? (
              <div className="customer-search-error-state">
                <strong>
                  Unable to load customers
                </strong>

                <span>
                  {requestError}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    fetchCustomers(search)
                  }
                >
                  Try Again
                </button>
              </div>
            ) : customers.length === 0 ? (
              <div className="customer-search-empty-state">
                <FiUser />

                <strong>
                  No customer found
                </strong>

                <span>
                  Add this customer before creating
                  the requirement.
                </span>
              </div>
            ) : (
              customers.map((customer) => {
                const customerName =
                  getCustomerName(customer);

                const customerMobile =
                  getCustomerMobile(customer);

                const isSelected =
                  String(value) ===
                  String(customer?._id);

                return (
                  <button
                    key={customer._id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`customer-search-result ${
                      isSelected
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      handleSelect(customer)
                    }
                  >
                    <div className="customer-search-avatar">
                      {String(customerName || "C")
                        .charAt(0)
                        .toUpperCase()}
                    </div>

                    <div className="customer-search-result-info">
                      <strong title={customerName}>
                        {customerName}
                      </strong>

                      <span>
                        Mobile: {customerMobile}
                      </span>

                      {customer?.companyName ? (
                        <small
                          title={
                            customer.companyName
                          }
                        >
                          {customer.companyName}
                        </small>
                      ) : null}
                    </div>

                    {isSelected ? (
                      <FiCheck className="customer-search-selected-icon" />
                    ) : null}
                  </button>
                );
              })
            )}
          </div>

          <div className="customer-search-footer">
            <button
              type="button"
              onClick={handleAddCustomer}
            >
              <FiPlus />

              Add New Customer
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CustomerSearchSelect;